import { Card, Tag } from 'antd';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { brandColors, darkColors, useThemeMode } from '@goelectric/ui-components';
import type { PowerPoint } from '../hooks/useNocLive';
import { formatClock, formatKw } from '../utils/format';

/**
 * Gráfica de potencia en streaming.
 *
 * Hoy el backend devuelve `power.totalInstantKw = 0` (sin MeterValues). La línea se
 * dibuja igual y, si no hay señal, se muestra una nota para no aparentar datos falsos.
 */
export function PowerChart({ data }: { data: PowerPoint[] }) {
  const { resolvedTheme } = useThemeMode();
  // Recharts no resuelve `var(--ge-*)` en atributos SVG: se elige el token por tema.
  const lineColor = resolvedTheme === 'dark' ? darkColors.primary : brandColors.info;
  const hasSignal = data.some((point) => point.kw > 0);
  const chartData = data.map((point) => ({ ...point, label: formatClock(point.t) }));

  return (
    <Card
      title="Potencia en streaming"
      extra={<Tag color={hasSignal ? 'green' : 'default'}>{hasSignal ? 'En vivo' : 'Sin señal'}</Tag>}
    >
      <div className="noc-chart">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="nocPowerFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lineColor} stopOpacity={0.45} />
                <stop offset="100%" stopColor={lineColor} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--ge-color-border)" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={28} />
            <YAxis tick={{ fontSize: 11 }} width={64} tickFormatter={(value: number) => `${value} kW`} />
            <Tooltip
              formatter={(value: number | string) => formatKw(Number(value))}
              labelFormatter={(label: string | number) => `Hora ${label}`}
            />
            <Area
              type="monotone"
              dataKey="kw"
              stroke={lineColor}
              strokeWidth={2}
              fill="url(#nocPowerFill)"
              isAnimationActive={false}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>

        {!hasSignal && (
          <div className="noc-chart__empty">
            La potencia en vivo aún no está disponible (MeterValues → TG-93/94). La gráfica se activará
            automáticamente al llegar telemetría.
          </div>
        )}
      </div>
    </Card>
  );
}
